import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '../ui/icons';
import './PinPad.css';

interface Props {
    value: string;
    onChange: (next: string) => void;
    /** Fired once `value` reaches `length`, so the pad submits itself. */
    onComplete: (value: string) => void;
    length?: number;
    disabled?: boolean;
    /** Set to shake the dots — bump the number to replay it. */
    errorAt?: number;
    autoFocusKeyboard?: boolean;
}

/** Long enough for the shake to finish; after that the pad looks untouched again. */
const SHAKE_MS = 450;

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * A numeric pad for entering a PIN, sized for thumbs on a tablet rather than a
 * mouse. It submits itself on the last digit: asking someone to type four digits
 * and then reach for a separate button is one interaction too many when it
 * happens fifty times a night.
 *
 * The pad is deliberately dumb — the parent owns the value and decides what a
 * completed PIN means.
 */
const PinPad: React.FC<Props> = ({
    value,
    onChange,
    onComplete,
    length = 4,
    disabled = false,
    errorAt = 0,
    autoFocusKeyboard = true,
}) => {
    /**
     * The error look has to be a moment, not a state. Left on, it repaints every
     * dot red — which reads as four digits still sitting in the field, refusing to
     * be deleted, and leaves nothing to show the next PIN being typed.
     */
    const [shaking, setShaking] = useState(false);
    useEffect(() => {
        if (!errorAt) return;
        setShaking(true);
        const id = window.setTimeout(() => setShaking(false), SHAKE_MS);
        return () => window.clearTimeout(id);
    }, [errorAt]);

    const press = useCallback((digit: string) => {
        if (disabled || value.length >= length) return;
        setShaking(false);
        const next = value + digit;
        onChange(next);
        if (next.length === length) onComplete(next);
    }, [disabled, value, length, onChange, onComplete]);

    const back = useCallback(() => {
        if (disabled || !value) return;
        onChange(value.slice(0, -1));
    }, [disabled, value, onChange]);

    // A physical keyboard is how this gets used on a desktop and during testing,
    // and it costs nothing to support.
    useEffect(() => {
        if (!autoFocusKeyboard) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key >= '0' && e.key <= '9') {
                press(e.key);
            } else if (e.key === 'Backspace') {
                e.preventDefault();
                back();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [press, back, autoFocusKeyboard]);

    return (
        <div className='pinpad'>
            <div
                className={`pinpad__dots${shaking ? ' is-error' : ''}`}
                key={errorAt}
                aria-label={`${value.length} of ${length} digits entered`}
            >
                {Array.from({ length }).map((_, i) => (
                    <span key={i} className={`pinpad__dot${i < value.length ? ' is-filled' : ''}`} />
                ))}
            </div>

            <div className='pinpad__keys'>
                {KEYS.map(k => (
                    <button
                        key={k}
                        type='button'
                        className='pinpad__key'
                        onClick={() => press(k)}
                        disabled={disabled}
                        aria-label={k}
                    >
                        {k}
                    </button>
                ))}
                <span className='pinpad__key pinpad__key--empty' aria-hidden='true' />
                <button
                    type='button'
                    className='pinpad__key'
                    onClick={() => press('0')}
                    disabled={disabled}
                    aria-label='0'
                >
                    0
                </button>
                <button
                    type='button'
                    className='pinpad__key pinpad__key--action'
                    onClick={back}
                    disabled={disabled || !value}
                    aria-label='Delete last digit'
                >
                    <Icon.Delete size={26} />
                </button>
            </div>
        </div>
    );
};

export default PinPad;
